import { useEffect, useRef, useState, useCallback } from 'react';

type MessageHandler = (data: unknown, eventType: string) => void;

interface UseSSEOptions {
  enabled: boolean;
  /** Event types to listen for (SSE named events). Falls back to onmessage if empty. */
  events?: string[];
  onMessage?: MessageHandler;
}

const MIN_DELAY = 1000;
const MAX_DELAY = 30000;

export function useSSE({ enabled, events, onMessage }: UseSSEOptions) {
  const [isConnected, setIsConnected] = useState(false);
  const sourceRef = useRef<EventSource | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const onMessageRef = useRef(onMessage);
  const attemptsRef = useRef(0);
  onMessageRef.current = onMessage;

  const connect = useCallback(() => {
    if (!enabled) return;

    // EventSource cannot send headers, so the app identifies itself via query param
    const source = new EventSource('/api/events/stream?app=rep');

    source.onopen = () => {
      setIsConnected(true);
      attemptsRef.current = 0;
    };

    const handler = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        onMessageRef.current?.(data, event.type);
      } catch {
        // ignore malformed messages (keepalive pings)
      }
    };

    // SSE named events require addEventListener; onmessage only fires for unnamed events
    if (events?.length) {
      for (const name of events) {
        source.addEventListener(name, handler);
      }
    } else {
      source.onmessage = handler;
    }

    source.onerror = () => {
      setIsConnected(false);
      source.close();
      const delay = Math.min(MIN_DELAY * 2 ** attemptsRef.current, MAX_DELAY);
      attemptsRef.current++;
      reconnectTimer.current = setTimeout(connect, delay);
    };

    sourceRef.current = source;
  }, [enabled, events]);

  useEffect(() => {
    connect();
    return () => {
      clearTimeout(reconnectTimer.current);
      sourceRef.current?.close();
    };
  }, [connect]);

  return { isConnected };
}
